import React from "react";
import GoogleMaps from "components/GoogleMaps";

const DAYS = [
  { key: "SUN", label: "Sun" },
  { key: "MON", label: "Mon" },
  { key: "TUE", label: "Tue" },
  { key: "WED", label: "Wed" },
  { key: "THU", label: "Thu" },
  { key: "FRI", label: "Fri" },
  { key: "SAT", label: "Sat" },
];

const SERVICE_LABELS = {
  photo: "Photo",
  video: "Video",
  drone: "Drone",
  floorplan: "Floorplan",
  floorplanRedraw: "Floorplan Redraw",
  edit: "Edit",
  print: "Print",
  auction: "Auction",
};

const to12Hour = (time24) => {
  if (!time24 || typeof time24 !== "string" || !time24.includes(":")) return "";
  const [hStr, mStr] = time24.split(":");
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${mStr || "00"} ${period}`;
};

const formatTimeForDisplay = (t) => {
  if (!t) return "";
  if (/AM|PM/i.test(t)) return t;
  return to12Hour(t);
};

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium text-darkgray uppercase tracking-wide">
      {label}
    </p>
    <div className="text-sm text-black">{children}</div>
  </div>
);

const Empty = ({ children = "Not provided" }) => (
  <span className="italic text-darkgray">{children}</span>
);

export default function ContractorView({ user, companies = [] }) {
  if (!user) return null;

  const formatRadius = (r) =>
    r >= 1000 ? `${(r / 1000).toFixed(1)} km` : `${r} m`;

  const priorityCenter = user.priorityArea?.lat
    ? { lat: user.priorityArea.lat, lng: user.priorityArea.lng }
    : null;
  const priorityRadius = user.priorityArea?.radius || 30000;

  // Resolve company name from the passed-in companies list (matching by id),
  // falling back to a populated companyId object if present.
  const companyId = user.companyId?._id || user.companyId;
  const companyName =
    companies.find((c) => c._id === companyId)?.name ||
    user.companyId?.name ||
    null;

  return (
    <div className="space-y-6">
      {/* ─── Profile ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <div className="flex items-center gap-4 mb-4">
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-16 h-16 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <span className="w-16 h-16 rounded-full bg-lightgray text-darkergray text-lg font-semibold flex items-center justify-center flex-shrink-0">
              {(user.name || "?").trim().charAt(0).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-base font-semibold text-black truncate">
              {user.name || "—"}
            </p>
            <span
              className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                user.status === "active"
                  ? "border-green-200 text-green-700 bg-green-50"
                  : "border-amber-200 text-amber-700 bg-amber-50"
              }`}
            >
              {user.status === "active" ? "Active" : "Invited"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Company">{companyName || <Empty />}</Field>

          <Field label="Email">
            {user.email ? (
              <a
                href={`mailto:${user.email}`}
                className="hover:underline break-all"
              >
                {user.email}
              </a>
            ) : (
              <Empty />
            )}
          </Field>

          <Field label="Mobile">
            {user.mobile ? (
              <a href={`tel:${user.mobile}`} className="hover:underline">
                {user.mobile}
              </a>
            ) : (
              <Empty />
            )}
          </Field>

          <Field label="Address">{user.address || <Empty />}</Field>
        </div>
      </div>

      {/* ─── Services ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <Field label="Services">
          {user.services?.length ? (
            <div className="flex flex-wrap gap-2 mt-1">
              {user.services.map((s) => (
                <span
                  key={s}
                  className="px-3 py-1 rounded-full text-xs font-medium border border-mediumgray text-black bg-white"
                >
                  {SERVICE_LABELS[s] || s}
                </span>
              ))}
            </div>
          ) : (
            <Empty>No services</Empty>
          )}
        </Field>
      </div>

      {/* ─── Calendar Color ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <Field label="Calendar Color">
          {user.calendarColor ? (
            <div className="flex items-center gap-2 mt-1">
              <div
                className="w-6 h-6 rounded-full border border-mediumgray"
                style={{ backgroundColor: user.calendarColor }}
              />
              <span className="font-mono text-xs">{user.calendarColor}</span>
            </div>
          ) : (
            <Empty>Not set</Empty>
          )}
        </Field>
      </div>

      {/* ─── Priority Area ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <Field label="Priority Area">
          {priorityCenter ? (
            <div className="space-y-3 mt-1">
              <div className="border border-mediumgray rounded-md overflow-hidden">
                <GoogleMaps
                  center={priorityCenter}
                  markers={[{ position: priorityCenter, iconType: "black" }]}
                  circles={[
                    {
                      center: priorityCenter,
                      radius: priorityRadius,
                      fillColor: user.calendarColor || "#000000",
                      fillOpacity: 0.15,
                      strokeColor: user.calendarColor || "#000000",
                      strokeWeight: 2,
                    },
                  ]}
                  options={{
                    view: "roadmap",
                    zoom: 11,
                    mapStyle: "customGrey",
                    disableDefaultUI: true,
                    zoomControl: true,
                    fullscreenControl: true,
                  }}
                />
              </div>
              <p className="text-xs text-darkgray">
                Radius: {formatRadius(priorityRadius)}
              </p>
            </div>
          ) : (
            <Empty>Not set</Empty>
          )}
        </Field>
      </div>

      {/* ─── Availability ─── */}
      <div className="rounded-lg border border-mediumgray p-4">
        <Field label="Availability">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
            {DAYS.map(({ key, label }) => {
              const day = user.availability?.[key];
              const isAvailable = day?.available;
              return (
                <div
                  key={key}
                  className={`border border-mediumgray rounded-md p-2 text-center ${
                    isAvailable ? "bg-lightergray" : ""
                  }`}
                >
                  <p className="text-xs uppercase tracking-wide text-darkgray mb-1">
                    {label}
                  </p>
                  {isAvailable ? (
                    <p className="text-xs text-black leading-tight">
                      {formatTimeForDisplay(day.startTime)}–
                      {formatTimeForDisplay(day.endTime)}
                    </p>
                  ) : (
                    <p className="text-xs text-darkgray">Off</p>
                  )}
                </div>
              );
            })}
          </div>
        </Field>
      </div>
    </div>
  );
}